import { Fragment, memo } from 'react';
import { ChevronRightIcon } from '@page/icons';
import { cn } from '../../lib';
import { Link } from 'react-router-dom';

export interface BreadcrumbItem {
  label: React.ReactNode;
  href?: string;
}

export interface BreadcrumbLinkProps {
  href: string;
  className: string;
  children: React.ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
  renderLink?: (props: BreadcrumbLinkProps) => React.ReactNode;
}

const defaultRenderLink = ({ href, className, children }: BreadcrumbLinkProps) => (
  <Link to={href} className={className}>
    {children}
  </Link>
);

const BreadcrumbBase = ({ items, separator, className, renderLink = defaultRenderLink }: BreadcrumbProps) => {
  if (!items.length) return null;

  return (
    <span className={cn('inline-flex items-center gap-1 font-body-md font-medium whitespace-nowrap', className)}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <Fragment key={index}>
            {item.href && !isLast ? (
              renderLink({
                href: item.href,
                className: cn(
                  'text-text-tertiary hover:text-text-primary hover:underline cursor-pointer transition-colors duration-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-border-input-select',
                ),
                children: item.label,
              })
            ) : (
              <span className={cn('flex items-center', isLast ? 'text-text-primary' : 'text-text-tertiary')}>
                {item.label}
              </span>
            )}

            {!isLast && (separator ?? <ChevronRightIcon className="size-4 fill-icon-disabled" />)}
          </Fragment>
        );
      })}
    </span>
  );
};

export const Breadcrumb = memo(BreadcrumbBase);
